import { Check, Copy } from 'lucide-react';
import { useState } from 'react';

/**
 * A generated link, shown once, with a copy button.
 *
 * The link is always visible as text rather than hidden behind the button alone.
 * Clipboard writes fail silently in more situations than people expect — an
 * insecure origin, a browser that requires a user gesture it did not see, a
 * locked-down device — and a leader who cannot select the text has no way to send
 * the invite at all.
 */
export function CopyLink({ url, label }: { url: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setFailed(false);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setFailed(true);
    }
  }

  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
      {label && (
        <span className="t-label" style={{ color: 'var(--color-text-muted)' }}>
          {label}
        </span>
      )}

      <div
        className="flex items-center gap-2"
        style={{
          border: '1.5px solid var(--color-border-strong)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-2) var(--space-2) var(--space-2) var(--space-3)',
          background: 'var(--color-surface-raised)',
        }}
      >
        <code
          className="t-caption flex-1 min-w-0"
          style={{
            color: 'var(--color-text-primary)',
            overflowWrap: 'anywhere',
            userSelect: 'all',
          }}
        >
          {url}
        </code>

        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center justify-center gap-2 shrink-0 rounded-md t-body-strong"
          style={{
            minHeight: '44px',
            minWidth: '44px',
            padding: '0 var(--space-3)',
            border: '1.5px solid var(--color-brand-ink)',
            background: 'transparent',
            color: 'var(--color-brand-ink)',
          }}
        >
          {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      {failed && (
        <p className="t-caption" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          Copying didn&rsquo;t work in this browser. Select the link above and copy it manually.
        </p>
      )}
    </div>
  );
}
